/**
 * REPSit.ai public API — reference data. Single source of truth for the in-app docs
 * (`pages/ApiDocs.jsx`) and for `docs/api/openapi.yaml` (`scripts/build-openapi.cjs`).
 * The implementation is `functions/src/hugoApi.js`; keep the two in step.
 */

export const API_VERSION = '2026-09-22';
export const BASE_PATH = '/api/v1';

export const SCOPES = [
  { key: 'organization:read', title: 'Organization structure', desc: 'Sites, departments, Natural Work Teams (NWTs) and the OE warning thresholds.' },
  { key: 'members:read', title: 'Members', desc: 'Names, emails, roles and scopes of the people in your organization.' },
  { key: 'scores:read', title: 'OE scores', desc: 'The latest REPS scores, the OE element breakdown, warnings and the snapshot history.' },
  { key: 'conditions:read', title: 'OE Conditions — read', desc: 'Every Drift, Trap, Roadblock and Landmine logged, with its review status.' },
  { key: 'conditions:write', title: 'OE Conditions — create', desc: 'Log a condition from another system. It enters the same L1 → L2 review as one typed in the app.' },
  { key: 'alerts:read', title: 'OE health alerts', desc: 'Active risk alerts DREW has raised, with recommended actions.' },
  { key: 'surveys:read', title: 'Surveys', desc: 'D&A Perspectives surveys — status, scope and response targets.' },
  { key: 'library:read', title: 'Learning Library', desc: 'Industry case studies plus documents your organization uploaded.' },
  { key: 'webhooks:manage', title: 'Webhooks', desc: 'Register, list, test and delete webhook endpoints.' },
];

export const ERROR_CODES = [
  ['400', 'invalid_parameter', 'A query parameter is malformed (bad date, unknown scope form…).'],
  ['401', 'unauthenticated / invalid_key / key_revoked / key_expired', 'No bearer key, or a key that is unknown, revoked or past its expiry.'],
  ['403', 'insufficient_scope', 'The key exists but was not granted the scope this route needs. `details.required` names it.'],
  ['404', 'not_found', 'No such route, or the object does not belong to your organization.'],
  ['405', 'method_not_allowed', 'The route exists but not with that HTTP method.'],
  ['409', 'limit_reached', 'A per-organization limit was hit (for example 10 webhooks).'],
  ['422', 'validation_failed', 'The request body did not pass validation. `details.errors` lists every problem.'],
  ['429', 'rate_limited', 'More than the key’s per-minute allowance. Retry after `X-RateLimit-Reset`.'],
  ['500', 'internal_error', 'Our side. Quote `X-Request-Id` when you tell the build team.'],
];

export const WEBHOOK_EVENTS = [
  { type: 'condition.created', when: 'An OE Condition is logged — in the app or through the API.', payload: 'Condition summary: id, conditionId, type, title, status, severity, scope, submittedByName, source.' },
  { type: 'condition.status_changed', when: 'A condition moves through review (L1 opened, approved, returned, resolved, cancelled…).', payload: 'Condition summary plus `previousStatus`.' },
  { type: 'alert.created', when: 'DREW raises an OE health alert (survey scores and confirmed conditions diverge, a pattern matches a case study).', payload: 'id, title, severity, summary, quadrants, scope, caseStudyId.' },
  { type: 'snapshot.created', when: 'OE scores are recalculated for a scope (after survey responses or a condition changing state).', payload: 'id, scope, period, scores {reliability, efficiency, productivity, safety, overall}, warnings.' },
];

const conditionExample = {
  id: 'Ab12Cd34Ef56',
  conditionId: '26-HOU-0007',
  kind: 'trl',
  type: 'trap',
  driftType: null,
  trapCategory: 'time_pressure',
  title: 'Tool crib closes during turnover',
  description: 'Crews on the 06:00 turnover cannot draw calibrated torque wrenches until 06:40; several jobs start with uncalibrated tools.',
  repsQuadrants: ['safety', 'productivity'],
  status: 'submitted',
  severity: { entrant: 'II', l1: null, l2: null },
  priority: null,
  dueDate: null,
  scope: { siteId: 'site_houston', deptId: 'dept_maint', nwtId: 'nwt_crew_a' },
  owner: { deptId: null, nwtId: null, uid: null, name: '' },
  submittedBy: { uid: 'apikey:K9x…', name: 'API · CMMS integration' },
  submittedAt: '2026-09-22T13:04:11.000Z',
  refActions: [],
  resolution: null,
  statusHistory: [{ status: 'submitted', at: '2026-09-22T13:04:11.000Z', byName: 'API · CMMS integration', note: 'Created through the REPSit.ai API' }],
  source: 'api',
  createdAt: '2026-09-22T13:04:11.000Z',
  updatedAt: '2026-09-22T13:04:11.000Z',
};

const snapshotExample = {
  id: 'snap_2026-09-22_org',
  scope: { type: 'organization', id: null },
  period: '2026-09',
  calculatedAt: '2026-09-22T06:00:00.000Z',
  scores: { overall: 4.6, reliability: 4.7, efficiency: 4.6, productivity: 4.6, safety: 4.7 },
  perceptionScores: { reliability: 4.5, efficiency: 4.4, productivity: 4.4, safety: 4.5 },
  conditionScores: { reliability: 0.2, efficiency: 0.2, productivity: 0.2, safety: 0.2 },
  elementScores: { driftPerception: { R: 4.3, E: 4.2, P: 4.4, S: 4.3 }, driftWork: { R: 5.2, E: 5.0, P: 5.1, S: 5.3 }, traps: { R: 4.6, E: 4.5, P: 4.7, S: 4.6 }, roadblocks: { R: 4.7, E: 4.8, P: 4.6, S: 4.7 }, landmines: { R: 4.4, E: 4.3, P: 4.4, S: 4.5 } },
  warnings: [],
  responseCount: 132,
  basis: { responseCount: 132, teamsCount: 29, headcount: 410, lowConfidence: false, text: '132 responses across 29 NWTs' },
};

export const ENDPOINTS = [
  {
    group: 'Getting started',
    items: [
      {
        id: 'me', method: 'GET', path: '/v1/me', scope: null, summary: 'Who am I?',
        desc: 'Returns the key you are using (label, scopes, last use) and the organization it belongs to. The first call to make: it proves the key works and shows which permissions it has.',
        response: { key: { id: 'K9xQ…', label: 'Power BI dashboard', scopes: ['organization:read', 'scores:read'], createdAt: '2026-09-22T10:00:00.000Z', lastUsedAt: '2026-09-22T13:04:11.000Z' }, organization: { id: 'org_magnum_t01', name: 'Magnum, Inc.', industry: 'Specialty Chemicals' }, apiVersion: API_VERSION },
      },
    ],
  },
  {
    group: 'Organization',
    items: [
      {
        id: 'organization', method: 'GET', path: '/v1/organization', scope: 'organization:read', summary: 'Structure and thresholds',
        desc: 'The organization’s sites (with the 2–4 letter site codes used in condition IDs), departments, Natural Work Teams and the OE warning thresholds. You need the `nwtId`s from here to create conditions.',
        response: { id: 'org_magnum_t01', name: 'Magnum, Inc.', industry: 'Specialty Chemicals', sites: [{ id: 'site_houston', name: 'Houston HQ', code: 'HOU', location: 'Houston, TX' }], departments: [{ id: 'dept_maint', name: 'Maintenance', siteId: 'site_houston' }], teams: [{ id: 'nwt_crew_a', name: 'Crew A', departmentId: 'dept_maint' }], thresholds: { reliability: 6, efficiency: 6, productivity: 6, safety: 6 }, createdAt: '2026-06-01T00:00:00.000Z' },
      },
      {
        id: 'members', method: 'GET', path: '/v1/members', scope: 'members:read', summary: 'Members and roles',
        params: [['role', 'query', 'Filter by role key: org_admin, executive, site_manager, department_lead, team_lead, respondent, org_it.'], ['site', 'query', 'Filter by site id.']],
        desc: 'Everyone in the organization with their role and scope (site / department / NWT). Emails are included — treat the response as personal data.',
        response: { data: [{ id: 'uid_123', email: 'j.blow@magnum.example', displayName: 'Joe Blow', firstName: 'Joe', lastName: 'Blow', title: 'Shift Supervisor', role: 'team_lead', scopeType: 'team', scopeId: 'nwt_crew_a', scopeIds: null, siteId: 'site_houston', deptId: 'dept_maint', nwtId: 'nwt_crew_a' }], count: 1 },
      },
    ],
  },
  {
    group: 'OE scores',
    items: [
      {
        id: 'scores', method: 'GET', path: '/v1/scores', scope: 'scores:read', summary: 'Latest scores for a scope',
        params: [['scope', 'query', '`organization` (default), `site:<id>`, `department:<id>` or `team:<id>`.']],
        desc: 'The most recent OE snapshot for the scope: the four REPS scores (0–10, higher = more Organizational Entropy = worse), the perception/reality split, the element breakdown and any threshold warnings. Returns `snapshot: null` when nothing has been calculated yet for that scope.',
        response: { scope: { type: 'organization', id: null }, snapshot: snapshotExample, thresholds: { reliability: 6, efficiency: 6, productivity: 6, safety: 6 } },
      },
      {
        id: 'snapshots', method: 'GET', path: '/v1/snapshots', scope: 'scores:read', summary: 'Score history',
        params: [['scope', 'query', 'As above.'], ['since', 'query', 'ISO-8601 date-time; only snapshots calculated at or after it.'], ['limit', 'query', '1–200, default 90.']],
        desc: 'Snapshots newest first — the series behind the OE Trends chart. Pull this into a BI tool to plot drift over time per site.',
        response: { scope: { type: 'site', id: 'site_houston' }, data: [snapshotExample], count: 1 },
      },
    ],
  },
  {
    group: 'OE Conditions',
    items: [
      {
        id: 'conditions-list', method: 'GET', path: '/v1/conditions', scope: 'conditions:read', summary: 'List conditions',
        params: [['status', 'query', 'submitted, review_l1, under_review, approved, resolution_pending, resolved, rejected, withdrawn, cancelled.'], ['site', 'query', 'Site id.'], ['type', 'query', 'drift, trap, roadblock, landmine.'], ['since', 'query', 'ISO-8601 date-time (createdAt ≥).'], ['limit', 'query', '1–200, default 50.']],
        desc: 'Conditions newest first. `severity` shows the entrant’s, L1’s and L2’s calls side by side (disagreement stays visible). `refActions` are the Reverse / Eliminate / Fortify actions chosen in review.',
        response: { data: [conditionExample], count: 1 },
      },
      {
        id: 'conditions-get', method: 'GET', path: '/v1/conditions/{id}', scope: 'conditions:read', summary: 'One condition',
        params: [['id', 'path', 'The document id or the human id (`26-HOU-0007`).']],
        desc: 'Full detail for one condition including its status history and resolution.',
        response: conditionExample,
      },
      {
        id: 'conditions-create', method: 'POST', path: '/v1/conditions', scope: 'conditions:write', summary: 'Log a condition',
        body: [
          ['type', 'string', 'required', 'drift · trap · roadblock · landmine'],
          ['title', 'string', 'required', '3–200 characters. Short and specific.'],
          ['description', 'string', 'required', '10–4000 characters. What was observed, when, and the consequence if it is not addressed.'],
          ['nwtId', 'string', 'required', 'The Natural Work Team where it was observed (from GET /v1/organization). Site and department are derived.'],
          ['repsQuadrants', 'string[]', 'required', 'One or more of reliability, efficiency, productivity, safety. The first is the primary quadrant.'],
          ['driftType', 'string', 'drift only', 'perception_of_risk · how_work_is_done · other'],
          ['trapCategory', 'string', 'trap only', 'time_pressure · distractions · multiple_tasks · overconfidence · vague_guidance · first_last_shift · peer_pressure · change_off_normal · physical_environment · mental_stress · fatigue'],
          ['severity', 'string', 'optional', 'The entrant’s assessment: I (low) · II (moderate, default) · III (high) · IV (critical). L1/L2 reviewers set the final level.'],
          ['ownerDeptId', 'string', 'optional', 'Recommended owning department. Reviewers can change it.'],
          ['submittedByName', 'string', 'optional', 'Shown as the entrant, e.g. the name of the person in your system. Defaults to “API · <key label>”.'],
          ['externalRef', 'string', 'optional', 'Your own id (work order, ticket) for cross-reference — stored, not interpreted.'],
        ],
        desc: 'Creates a condition in status `submitted`, allocates its human id (`YY-SSS-NNNN`) from the site’s sequence and routes it to L1 review exactly like a condition typed in the Entry Portal. The activity log records the key that created it; DREW’s analysis runs on it the same way.',
        request: { type: 'trap', trapCategory: 'time_pressure', title: 'Tool crib closes during turnover', description: 'Crews on the 06:00 turnover cannot draw calibrated torque wrenches until 06:40; several jobs start with uncalibrated tools.', nwtId: 'nwt_crew_a', repsQuadrants: ['safety', 'productivity'], severity: 'II', submittedByName: 'Joe Blow (CMMS)', externalRef: 'WO-88213' },
        response: conditionExample,
        status: 201,
      },
    ],
  },
  {
    group: 'Alerts, surveys, library',
    items: [
      {
        id: 'alerts', method: 'GET', path: '/v1/alerts', scope: 'alerts:read', summary: 'OE health alerts',
        params: [['status', 'query', 'active (default) or acknowledged.'], ['limit', 'query', '1–200, default 50.']],
        desc: 'Alerts DREW has raised for the organization, newest first, with recommended actions and a matching case study where there is one.',
        response: { data: [{ id: 'alert_1', title: 'Accumulating shift-handover drift — Pasadena Plant', severity: 'critical', status: 'active', summary: 'Shift-handover communication drift at Pasadena Plant has crossed OE thresholds.', description: '…', recommendedActions: ['Reinstate the 15-minute overlap on all four crews', 'Audit handover checklists this week'], quadrants: ['safety', 'reliability'], scope: { siteId: 'site_pasadena', deptId: null, nwtId: null }, caseStudyId: 'cs_texas_city', createdAt: '2026-09-20T05:30:00.000Z' }], count: 1 },
      },
      {
        id: 'surveys', method: 'GET', path: '/v1/surveys', scope: 'surveys:read', summary: 'Surveys',
        params: [['status', 'query', 'active, draft or closed.'], ['limit', 'query', '1–200, default 50.']],
        desc: 'D&A Perspectives surveys and their scope. Individual responses are anonymous and are not exposed by the API.',
        response: { data: [{ id: 'survey_2026-09', title: 'Drift & Accumulation Survey — September 2026', status: 'active', description: 'Monthly organizational-entropy pulse.', scope: { type: 'organization', id: null }, targetResponses: 400, dueDate: '2026-09-30', createdAt: '2026-09-01T00:00:00.000Z' }], count: 1 },
      },
      {
        id: 'case-studies', method: 'GET', path: '/v1/case-studies', scope: 'library:read', summary: 'Learning Library',
        params: [['limit', 'query', '1–200, default 50.']],
        desc: 'Industry case studies shared with every organization plus the documents your organization uploaded. Full text is not returned; `url` points at the source where available.',
        response: { data: [{ id: 'cs_texas_city', title: 'BP Texas City Refinery Explosion — 2005', industry: 'Oil & Gas', source: 'CSB Investigation', incidentDate: '2005-03-23', summary: '…', repsScores: { R: 9, E: 7, P: 6, S: 10 }, visibility: 'global', url: 'https://www.csb.gov/…' }], count: 1 },
      },
    ],
  },
  {
    group: 'Webhooks',
    items: [
      {
        id: 'webhooks-list', method: 'GET', path: '/v1/webhooks', scope: 'webhooks:manage', summary: 'List webhooks',
        desc: 'Every endpoint registered for the organization with its delivery health. Secrets are never returned after creation.',
        response: { data: [{ id: 'wh_1', url: 'https://hooks.example.com/repsit', events: ['condition.created', 'alert.created'], active: true, description: 'Teams channel', createdAt: '2026-09-22T10:00:00.000Z', lastDeliveryAt: '2026-09-22T13:04:12.000Z', lastDeliveryStatus: 200, failureCount: 0 }], events: WEBHOOK_EVENTS.map((e) => e.type) },
      },
      {
        id: 'webhooks-create', method: 'POST', path: '/v1/webhooks', scope: 'webhooks:manage', summary: 'Register a webhook',
        body: [['url', 'string', 'required', 'An https:// endpoint that answers 2xx within 8 seconds.'], ['events', 'string[]', 'optional', 'Subset of the event types; default all.'], ['description', 'string', 'optional', 'Up to 200 characters.']],
        desc: 'Registers an endpoint and returns its signing `secret` — the only time it is shown. Up to 10 per organization. After 25 consecutive failures the webhook is paused; register it again to resume.',
        request: { url: 'https://hooks.example.com/repsit', events: ['condition.created', 'condition.status_changed', 'alert.created'], description: 'Teams channel' },
        response: { id: 'wh_1', url: 'https://hooks.example.com/repsit', events: ['condition.created', 'condition.status_changed', 'alert.created'], active: true, description: 'Teams channel', createdAt: '2026-09-22T10:00:00.000Z', lastDeliveryAt: null, lastDeliveryStatus: null, failureCount: 0, secret: 'whsec_3f9c…' },
        status: 201,
      },
      {
        id: 'webhooks-test', method: 'POST', path: '/v1/webhooks/{id}/test', scope: 'webhooks:manage', summary: 'Send a test event',
        desc: 'Delivers a `ping` event to the endpoint with a real signature so you can verify your receiver end to end.',
        response: { delivered: true, status: 200, durationMs: 143 },
      },
      {
        id: 'webhooks-delete', method: 'DELETE', path: '/v1/webhooks/{id}', scope: 'webhooks:manage', summary: 'Delete a webhook',
        desc: 'Stops deliveries immediately. Responds 204 with no body.',
        status: 204,
      },
    ],
  },
];

export const VERIFY_SIGNATURE_SAMPLES = {
  node: `import crypto from 'node:crypto';

export function verifyRepsitSignature(req, secret) {
  const ts = req.get('X-REPSit-Timestamp');
  const sig = (req.get('X-REPSit-Signature') || '').replace(/^v1=/, '');
  if (Math.abs(Date.now() / 1000 - Number(ts)) > 300) return false;        // 5-minute replay window
  const expected = crypto.createHmac('sha256', secret).update(\`\${ts}.\${req.rawBody}\`).digest('hex');
  return crypto.timingSafeEqual(Buffer.from(expected, 'hex'), Buffer.from(sig, 'hex'));
}`,
  python: `import hmac, hashlib, time

def verify_repsit_signature(headers, raw_body: bytes, secret: str) -> bool:
    ts = headers.get("X-REPSit-Timestamp", "")
    sig = headers.get("X-REPSit-Signature", "").removeprefix("v1=")
    if abs(time.time() - float(ts or 0)) > 300:
        return False
    expected = hmac.new(secret.encode(), f"{ts}.".encode() + raw_body, hashlib.sha256).hexdigest()
    return hmac.compare_digest(expected, sig)`,
};
